import { useEffect, useRef, useState } from 'react';
import { request } from './twinscope-api.js';
import { supervisorAccessLost } from './telemetry-access.js';
import { useI18n } from './i18n.jsx';
import { metrics, alarmLevel } from './twin-metrics.js';
import { TelemetryTabs } from './TelemetryTabs.jsx';
import './telemetry-views.css';

export function TwinDataViewer({ twinId, plantId, name, config, onBack, onDenied }) {
  const { t, language } = useI18n();
  const denied = useRef(onDenied);
  denied.current = onDenied;
  const [snapshot, setSnapshot] = useState(null);
  const [samples,setSamples] = useState([]);
  const lastSample = useRef('');
  useEffect(()=>{setSamples([]);setSnapshot(null);lastSample.current='';},[twinId,plantId]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true, timer;
    const controller = new AbortController();
    const base = `/api/twinscope/twins/${encodeURIComponent(twinId)}/realtime/`;
    const query = `?plantId=${encodeURIComponent(plantId)}&objectId=`;
    async function poll() {
      setLoading(true);
      try {
        const status = await request(base + 'status' + query, { signal: controller.signal });
        let latest = null;
        if (status.hasData || status.available) {
          try { latest = await request(base + 'latest' + query, { signal: controller.signal }); }
          catch (cause) { if (cause.status !== 404) throw cause; }
        }
        if (active) {
          setSnapshot({ status, latest, fetchedAt: Date.now() }); setError('');
          if(latest?.payload && latest.payload.encrypted!==true && !(latest.encryption?.encrypted && !latest.encryption?.decrypted)) {
            const identity=JSON.stringify([latest.receivedAt,latest.payload]);
            if(identity!==lastSample.current) {lastSample.current=identity;setSamples(current=>[...current,latest.payload].slice(-30));}
          }
        }
      } catch (cause) {
        if (!active) return;
        if ([401, 403].includes(cause.status)) {
          setSnapshot(null); setSamples([]); lastSample.current='';
          const lostAccess = await supervisorAccessLost(cause, { signal: controller.signal });
          if (!active) return;
          if (lostAccess) { active = false; denied.current(); return; }
        }
        setError('twinDataError');
      } finally {
        if (active) { setLoading(false); timer = setTimeout(poll, 5000); }
      }
    }
    void poll();
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [twinId, plantId, refresh]);
  const { status, latest } = snapshot || {};
  const date = latest?.receivedAt || status?.lastSeenAt;
  const formattedDate = date && !Number.isNaN(new Date(date).getTime()) ? new Date(date).toLocaleString(language === 'it' ? 'it-IT' : 'en-GB') : '—';
  const payload = latest?.payload;
  const encrypted = (latest?.encryption?.encrypted && !latest?.encryption?.decrypted) || payload?.encrypted === true;
  const rules = config?.alarmRules || [];
  const fields = metrics(payload, rules, language);
  const chartSamples=samples.map(sample=>({observedAt:sample.observedAt,measurements:Object.fromEntries(metrics(sample,rules,language).map(row=>[row.key,{value:row.value,label:row.label,unit:row.unit}]))}));
  const level = error ? 'offline' : alarmLevel(snapshot, rules);
  const display = value => typeof value === 'number' ? new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-GB', { maximumFractionDigits: 2 }).format(value) : typeof value === 'boolean' ? t(value ? 'valueYes' : 'valueNo') : String(value);
  return <section className="document-viewer twin-data-viewer"><header><button onClick={onBack}>← {t('backToImage')}</button><strong>{name} · {t('data')}</strong><button disabled={loading} onClick={() => setRefresh(value => value + 1)}>{t('refreshTwinData')}</button></header><div className="native-twin-data" aria-busy={loading}>
    <p className="twin-embed-note">{t('localTwinDataHelp')}</p>
    {error && <p role="alert" className="form-error">{t(error)}</p>}
    <div className="twin-data-summary"><section><span>{t('dataQuality')}</span><strong>{t(error ? 'twinDataUnavailable' : status?.connected ? 'live' : status?.hasData ? 'twinDataStale' : loading && !snapshot ? 'pleaseWait' : 'waitingTelemetry')}</strong></section><section><span>{t('lastUpdate')}</span><strong>{formattedDate}</strong></section></div>
    <div className="native-twin-id"><span>{t('digitalTwinObjectId')}</span><code>{twinId}</code></div>
    {latest?.source && <p>{t('source')}: {latest.source.type} · {latest.source.address}</p>}
    <p className={`alarm-state ${level}`}>{t('alarmState')}: <strong>{t(`level_${level}`)}</strong></p>
    {payload?.operatingState && <p>{t('operatingState')}: {String(payload.operatingState)}</p>}
    {encrypted ? <p role="alert">{t('twinDataEncrypted')}</p> : fields.length ? <TelemetryTabs key={`${plantId}:${twinId}`} samples={chartSamples} language={language}><div className="twin-values">{fields.map(row => <article className={`metric-card ${typeof row.value==='number'?'':'metric-text'} ${level === 'offline' ? 'offline' : row.level}`} key={row.key}><h3>{row.label}</h3><div className="metric-reading"><strong>{display(row.value)}</strong><span>{row.unit}</span></div><small>{t(`level_${level === 'offline' ? 'offline' : row.level}`)}</small>{rules.filter(rule => rule.key === row.key).map(rule => <p className="metric-limits" key={rule.key}>{['criticalMin','warningMin','warningMax','criticalMax'].filter(key => rule[key] !== undefined).map(key => `${t(key)}: ${display(rule[key])}`).join(' · ')}</p>)}</article>)}</div></TelemetryTabs> : <p>{t('waitingTelemetry')}</p>}
    {payload?.position?.coordinates && <p>{t('coordinates')}: {payload.position.coordinates.map(display).join(', ')}</p>}
    {!encrypted && latest && <details><summary>{t('rawTwinData')}</summary><pre className="raw-twin-data">{JSON.stringify(latest, null, 2)}</pre></details>}
  </div></section>;
}
